import { LinkifyIt } from 'linkify-it';

import type { LinkKind } from '@pressreader/content-types';
import { isNullOrWhitespace } from '@pressreader/utils';

import { normalizeInvisibleChars } from './invisible.chars';
import { TopLevelDomains } from './top.level.domains';

interface LinkMatch {
    /** Inclusive offset in the source text. */
    start: number;
    /** Exclusive offset in the source text. */
    end: number;
    kind: LinkKind;
    href: string;
}

/** Half-open range of the buffer that a pass has claimed, whether or not it produced a match. */
interface ConsumedRange {
    start: number;
    end: number;
}

const MailtoScheme = 'mailto:';

// Web and email recognition is delegated to linkify-it. The curated list is handed to `.tlds()`,
// which gates fuzzy links only: the fuzzy mail rule accepts any letter run as a host, so email
// domains are filtered against the same list in `isAcceptableEmailAddress`. The protocol-relative
// rule is switched off: `//host` was never a link in the legacy detector and reads as prose far more
// often than as a URL.
let linkifier: LinkifyIt | null = null;

/** Built on first use: compiling the rule set is not free, and some article loads never call into detection at all. */
function getLinkifier(): LinkifyIt {
    if (linkifier === null) {
        linkifier = new LinkifyIt({ fuzzyLink: true, fuzzyEmail: true, fuzzyIP: false }).tlds([...TopLevelDomains]).add('//', null);
    }

    return linkifier;
}

const phoneRegex = /(?:(?:\+\d{1,4}[ \u00A0]?)?\(\d{2,4}\)|\+?\d{1,4})(?:[ \u00A0.-]\d{2,4})+/g;
/** The default linkify-it schemas do not cover `tel:`/`callto:`; typed ones get a dedicated pass, with the prefix captured to mask it. */
const schemedPhoneRegex = /(tel:|callto:)(\+?\d[\d \u00A0().-]{3,18}\d)/gi;

const htmlAmpersandRegex = /&amp;/gi;
const digitClusterRegex = /\d+/g;
const nonDigitRegex = /\D/g;
const phoneNoiseRegex = /[\d()+]/g;
const nonBreakingSpaceRegex = /\u00A0/g;
const hostEndRegex = /[/?#:]/;
const numericLabelRegex = /^\d+$/;

/** Day-first and year-first dates share the phone grouping shapes, so they are excluded explicitly. */
const dateShapeRegexes = [/^\d{1,2}[.-]\d{1,2}[.-]\d{2,4}$/, /^\d{4}[.-]\d{1,2}[.-]\d{1,2}$/];
/** Dot grouping is otherwise ambiguous with versions and decimals; only the NANP shape is unambiguous. */
const nanpClusterShapes = ['3-3-4', '1-3-3-4'];
const phoneBorderRegex = /[\p{L}\p{N}\p{M}_/%\p{Sc}]/u;

const PhoneMinDigits = 7;
const PhoneMaxDigits = 15;
const PhoneMaxClusters = 4;
const ThousandsGroupSize = 3;
const SchemedPhoneMinDigits = 5;

const MaskChar = '\0';

/**
 * Overwrites the given ranges with same-length filler so later passes skip them while offsets keep
 * indexing the source text. The ranges must be ordered and non-overlapping, which is how both passes
 * produce them; the buffer is rebuilt once, not once per range.
 */
function maskRanges(buffer: string, ranges: readonly ConsumedRange[]): string {
    if (ranges.length === 0) {
        return buffer;
    }

    const parts: string[] = [];
    let cursor = 0;

    for (const range of ranges) {
        parts.push(buffer.slice(cursor, range.start), MaskChar.repeat(range.end - range.start));
        cursor = range.end;
    }
    parts.push(buffer.slice(cursor));

    return parts.join('');
}

function buildEmailHref(address: string): string {
    const domainSeparatorIndex = address.lastIndexOf('@');
    const localPart = address.slice(0, domainSeparatorIndex);
    const domain = address.slice(domainSeparatorIndex + 1);

    // The local part admits `?`, `#`, `%` and `/`, which would otherwise turn into mailto query
    // syntax; the domain needs no encoding, its charset is `[\w-.]`.
    return `${MailtoScheme}${encodeURIComponent(localPart)}@${domain}`;
}

/**
 * A fuzzy match carries no scheme to prove intent, so it also has to look like a host rather than the
 * seam between two sentences: OCR and missing spaces glue a capitalized word onto the word before it
 * (`world.Today`), and a purely numeric run in front of one reads as a year or an amount
 * (`1999.World`). Both forms are hosts as far as the grammar is concerned.
 */
function isHostShapedFuzzyMatch(raw: string): boolean {
    const hostEnd = raw.search(hostEndRegex);
    const labels = (hostEnd < 0 ? raw : raw.slice(0, hostEnd)).split('.');
    const topLevelDomain = labels[labels.length - 1];

    // How a host is typed: `example.com` or `EXAMPLE.COM`. `example.Com` is a sentence boundary.
    if (topLevelDomain !== topLevelDomain.toLowerCase() && topLevelDomain !== topLevelDomain.toUpperCase()) {
        return false;
    }

    return labels.slice(0, -1).some(label => !numericLabelRegex.test(label));
}

/** linkify-it accepts any letter run as a fuzzy mail host, so the curated list is applied here. */
function isAcceptableEmailAddress(address: string): boolean {
    const domainSeparatorIndex = address.lastIndexOf('@');

    // A local part holding a path separator is a url the mail rule swallowed (`example.com/u@v.com`).
    if (address.slice(0, domainSeparatorIndex).includes('/')) {
        return false;
    }

    const domain = address.slice(domainSeparatorIndex + 1);

    return TopLevelDomains.has(domain.slice(domain.lastIndexOf('.') + 1).toLowerCase());
}

function buildPhoneHref(phone: string): string {
    const digits = phone.replace(nonDigitRegex, '');

    return phone.startsWith('+') ? `tel:+${digits}` : `tel:${digits}`;
}

function isAcceptablePhoneShape(phone: string): boolean {
    const clusters: readonly string[] = phone.match(digitClusterRegex) ?? [];
    if (clusters.length < 2 || clusters.length > PhoneMaxClusters) {
        return false;
    }

    const totalDigits = clusters.reduce((total, cluster) => total + cluster.length, 0);
    if (totalDigits < PhoneMinDigits || totalDigits > PhoneMaxDigits) {
        return false;
    }

    if (dateShapeRegexes.some(regex => regex.test(phone))) {
        return false;
    }

    const isAnchored = phone.startsWith('+') || phone.includes('(');

    // Two bare clusters are as likely a score, a range or a decimal as a phone number.
    if (clusters.length === 2 && !isAnchored) {
        return false;
    }

    const separators = new Set(phone.replace(phoneNoiseRegex, '').replace(nonBreakingSpaceRegex, ' '));
    if (separators.size > 1 && !isAnchored) {
        return false;
    }

    // A thousands-grouped amount (`1 000 000`) is a space-separated cluster run like a phone number;
    // only a trailing cluster that is not a thousands group tells the two apart.
    const isSpaceGrouped = separators.size === 1 && separators.has(' ');
    if (isSpaceGrouped && !isAnchored && clusters.slice(1).every(cluster => cluster.length === ThousandsGroupSize)) {
        return false;
    }

    if (separators.has('.') && separators.size === 1 && !isAnchored) {
        return nanpClusterShapes.includes(clusters.map(cluster => cluster.length).join('-'));
    }

    return true;
}

function hasBlockingBorder(source: string, start: number, end: number): boolean {
    const before = start > 0 ? source[start - 1] : '';
    const after = end < source.length ? source[end] : '';

    return phoneBorderRegex.test(before) || phoneBorderRegex.test(after);
}

/** Each fuzzy rule recognizes more than it should, in its own way, so each answers to its own guard. */
function isAcceptableWebLink(schema: string, candidate: string): boolean {
    if (schema === MailtoScheme) {
        return isAcceptableEmailAddress(candidate);
    }

    return schema !== '' || isHostShapedFuzzyMatch(candidate);
}

function collectWebLinks(buffer: string, matches: LinkMatch[]): string {
    const consumed: ConsumedRange[] = [];

    for (const match of getLinkifier().match(buffer) ?? []) {
        // A shape linkify-it recognized never falls through to a later pass, even when rejected below.
        consumed.push({ start: match.index, end: match.lastIndex });

        const isEmail = match.schema === MailtoScheme;
        // linkify-it covers a typed `mailto:` prefix, our contract does not: a range-based markup
        // model cannot hide display characters, so the prefix stays plain text before the link.
        const prefixLength = isEmail && match.raw.toLowerCase().startsWith(MailtoScheme) ? MailtoScheme.length : 0;
        const candidate = match.raw.slice(prefixLength);

        if (!isAcceptableWebLink(match.schema, candidate)) {
            continue;
        }

        const href = isEmail ? buildEmailHref(candidate) : match.url.replace(htmlAmpersandRegex, '&');
        matches.push({ start: match.index + prefixLength, end: match.lastIndex, kind: isEmail ? 'email' : 'url', href });
    }

    return maskRanges(buffer, consumed);
}

/**
 * A typed `tel:`/`callto:` scheme states the intent the bare pass has to infer from grouping, so the
 * shape rules do not apply here — the compact form these are printed in (`tel:+16045551234`) is
 * exactly what the bare pass rejects. Only the digit count is checked, to keep noise out.
 */
function collectSchemedPhones(buffer: string, matches: LinkMatch[]): string {
    const consumed: ConsumedRange[] = [];
    schemedPhoneRegex.lastIndex = 0;

    let match = schemedPhoneRegex.exec(buffer);
    while (match !== null) {
        const phone = match[2];
        // The scheme stays plain text before the link, the way a verbatim `mailto:` prefix does.
        const start = match.index + match[1].length;
        const end = start + phone.length;

        consumed.push({ start: match.index, end });

        if (phone.replace(nonDigitRegex, '').length >= SchemedPhoneMinDigits) {
            matches.push({ start, end, kind: 'phone', href: buildPhoneHref(phone) });
        }

        match = schemedPhoneRegex.exec(buffer);
    }

    return maskRanges(buffer, consumed);
}

function collectPhones(source: string, buffer: string, matches: LinkMatch[]): void {
    phoneRegex.lastIndex = 0;

    let match = phoneRegex.exec(buffer);
    while (match !== null) {
        const phone = match[0];
        const start = match.index;
        const end = start + phone.length;

        if (isAcceptablePhoneShape(phone) && !hasBlockingBorder(source, start, end)) {
            matches.push({ start, end, kind: 'phone', href: buildPhoneHref(phone) });
        }

        match = phoneRegex.exec(buffer);
    }
}

/**
 * Detects web links, emails and phone numbers in plain text.
 * @param text - Source text; authorless articles and captionless images pass empty values here.
 * @returns Non-overlapping matches ordered by start offset, indexing the source text with an exclusive end.
 */
function detectLinks(text?: string | null): LinkMatch[] {
    if (!text || isNullOrWhitespace(text)) {
        return [];
    }

    // Every pass runs on the normalized text, so a soft hyphen inside a host or address neither
    // breaks the pattern nor reaches the href; the ranges are mapped back before they are returned.
    const { clean, toOriginal } = normalizeInvisibleChars(text);

    const matches: LinkMatch[] = [];
    const withoutWebLinks = collectWebLinks(clean, matches);
    const withoutSchemedPhones = collectSchemedPhones(withoutWebLinks, matches);
    collectPhones(clean, withoutSchemedPhones, matches);

    return matches
        .sort((left, right) => left.start - right.start)
        .map(match => ({ ...match, start: toOriginal(match.start), end: toOriginal(match.end - 1) + 1 }));
}

export { detectLinks };
export type { LinkMatch };
